import { useEffect, useState } from "react";

import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  FileText,
  Plus,
  Search,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { ScrollArea } from "~/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { StatCardsSkeleton, TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import CreatePurchaseOrderDrawer from "~/pages/clients/components/client-detail/purchase-order/CreatePurchaseOrderDrawer";
import { useDispatchGuide } from "~/pages/operations/hooks/useDispatchGuide";
import { CreateDispatchGuideDialog } from "~/pages/purchase-orders/components/order-detail/CreateDispatchGuideDialog";
import { useSalesRequests } from "~/pages/purchase-orders/hooks/useSalesRequests";
import OrderDetailDialog from "~/pages/purchase-orders/OrderDetailDialog";
import { Client, SalesRequest, SalesRequestItem } from "~/types/client";
import { TransportData } from "~/types/dispatch";
import { formatCurrency } from "~/utils/helpers";
import { grossFromNet } from "~/utils/iva";

const GuiasDespachoPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ready_for_dispatch");
  const [selectedOrder, setSelectedOrder] = useState<SalesRequest | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [purchaseOrderDrawerOpen, setPurchaseOrderDrawerOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  // Pagination and sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string>("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const itemsPerPage = 10;

  // Fetch data from Supabase
  const {
    salesRequests,
    salesRequestItems,
    clientNames,
    clientInfo,
    clientRiskStatus,
    isLoading,
    getSellerInfo,
    getApproverRejecterInfo,
    fetchSalesRequests,
    fetchItemsForSalesRequest,
  } = useSalesRequests();

  const { isCreatingDispatchGuide, createDispatchGuide } = useDispatchGuide({
    refreshPageWithParams: true,
  });

  // Order whose guía-creation dialog (transport data) is open.
  const [guiaDialogOrder, setGuiaDialogOrder] = useState<SalesRequest | null>(
    null
  );

  const handleConfirmGuia = async (
    transport: TransportData,
    observations: string
  ) => {
    if (!guiaDialogOrder) return;
    try {
      await createDispatchGuide(guiaDialogOrder, transport, observations);
      setGuiaDialogOrder(null);
    } catch (error) {
      console.error("Failed to create dispatch guide:", error);
    }
  };

  // Check URL parameters on component mount to auto-open order detail
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderIdToOpen = urlParams.get("open");
    const guiaGenerated = urlParams.get("guia_generated");

    if (
      orderIdToOpen &&
      guiaGenerated === "true" &&
      salesRequests.length > 0 &&
      !isLoading
    ) {
      // Find the order in the sales requests
      const orderToOpen = salesRequests.find(
        (order) => order.id === orderIdToOpen
      );
      if (orderToOpen) {
        setSelectedOrder(orderToOpen);
        // Fetch items for the order
        fetchItemsForSalesRequest(orderToOpen.id);
      }

      // Clean up URL parameters
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("open");
      newUrl.searchParams.delete("guia_generated");
      window.history.replaceState({}, "", newUrl.toString());
    }
  }, [salesRequests, fetchItemsForSalesRequest, isLoading]);

  // Helper function to get client name
  const getClientName = (clientId: string) => {
    return clientNames[clientId] || "Cliente desconocido";
  };

  // Load clients when dialog opens
  const loadClients = async (searchTerm: string = "") => {
    setClientsLoading(true);
    try {
      let query = supabase
        .from("clients")
        .select("*")
        .is("deleted_at", null)
        .order("name");

      if (searchTerm.length >= 2) {
        const normalizedSearch = searchTerm.toLowerCase().trim();
        query = query.or(
          `name.ilike.%${normalizedSearch}%,tax_id.ilike.%${normalizedSearch}%`
        );
      } else {
        // Load recent clients if no search term
        query = query.limit(20);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Map database fields to Client interface
      const mappedClients = (data || []).map((client) => ({
        ...client,
        company: client.name || "", // Map name to company
        phone: client.phone_number || "",
        assigned_seller: client.assigned_seller || "",
        taxId: client.tax_id || "",
        insuredAmount: client.insured_amount || 0,
        salesConditionId: client.sales_condition_id || "",
        creditScore: 5, // Mock data
        riskScore: 3, // Mock data
        paymentHistory: "good" as const, // Mock data
        isActive: true,
      }));

      setAvailableClients(mappedClients);
    } catch (error) {
      console.error("Error loading clients:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los clientes",
        variant: "destructive",
      });
    } finally {
      setClientsLoading(false);
    }
  };

  // Handle dialog open
  const handleCreateDialogOpen = () => {
    setCreateDialogOpen(true);
    setClientSearch("");
    setSelectedClientId("");
    loadClients(); // Load initial clients
  };

  // Handle client search
  const handleClientSearchChange = (value: string) => {
    setClientSearch(value);
    if (value.length >= 2 || value.length === 0) {
      loadClients(value);
    }
  };

  // Fetch client details when client is selected
  const handleClientSelect = async (clientId: string) => {
    if (!clientId) return;

    try {
      const client = availableClients.find((c) => c.id === clientId);
      if (!client) {
        // Fetch from database if not in local list
        const { data: fetchedClient, error } = await supabase
          .from("clients")
          .select("*")
          .eq("id", clientId)
          .single();

        if (error) throw error;
        console.log("Fetched client from database:", fetchedClient);

        // Map database fields to Client interface
        const mappedClient = {
          ...fetchedClient,
          company: fetchedClient.name || "", // Map name to company
          phone: fetchedClient.phone_number || "",
          assigned_seller: fetchedClient.assigned_seller || "",
          taxId: fetchedClient.tax_id || "",
          insuredAmount: fetchedClient.insured_amount || 0,
          salesConditionId: fetchedClient.sales_condition_id || "",
          creditScore: 5, // Mock data
          riskScore: 3, // Mock data
          paymentHistory: "good" as const, // Mock data
          isActive: true,
        };

        setSelectedClient(mappedClient);
      } else {
        console.log("Selected client from list:", client);
        // Ensure client from list also has company field
        const mappedClient = {
          ...client,
          company: client.company || client.name || "",
        };
        setSelectedClient(mappedClient);
      }

      setCreateDialogOpen(false);
      setPurchaseOrderDrawerOpen(true);
    } catch (error) {
      console.error("Error fetching client:", error);
      toast({
        title: "Error",
        description: "No se pudo cargar la información del cliente",
        variant: "destructive",
      });
    }
  };

  // Handle order creation and auto-create dispatch guide
  const handleOrderCreated = async () => {
    // Refresh orders list
    await fetchSalesRequests();

    // Close drawer
    setPurchaseOrderDrawerOpen(false);
    setSelectedClient(null);
    setSelectedClientId("");

    // Show success message
    toast({
      title: "¡Pedido creado!",
      description:
        "El pedido se ha creado exitosamente. Ahora puedes crear su guía de despacho.",
      variant: "default",
    });
  };

  // Filter available clients based on search
  const filteredClients = availableClients.filter(
    (client) =>
      client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
      (client.taxId &&
        client.taxId.toLowerCase().includes(clientSearch.toLowerCase()))
  );

  // Local state for order items
  const [currentOrderItems, setCurrentOrderItems] = useState<
    SalesRequestItem[]
  >([]);
  const [loadingOrderItems, setLoadingOrderItems] = useState(false);

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Filter and sort orders by search query and status
  const filteredAndSortedOrders = salesRequests
    .filter((order) => {
      const matchesSearch =
        order.clientId
          ?.toString()
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        getClientName(order.clientId)
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        order.id.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ||
        order.status === statusFilter ||
        (statusFilter === "ready_for_dispatch" &&
          ["accepted", "por_facturar"].includes(order.status));

      return matchesSearch && matchesStatus && order.status !== "test";
    })
    .sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case "id":
          aValue = a.id;
          bValue = b.id;
          break;
        case "client":
          aValue = getClientName(a.clientId);
          bValue = getClientName(b.clientId);
          break;
        case "createdAt":
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case "status":
          aValue = a.status;
          bValue = b.status;
          break;
        case "amount":
          aValue = grossFromNet(a.totalAmount, a.isExento);
          bValue = grossFromNet(b.totalAmount, b.isExento);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = filteredAndSortedOrders.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  // Get status badge component
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { label: "Pendiente", color: "bg-amber-100 text-amber-800" },
      dispatch_ready: { label: "Con Guía", color: "bg-blue-100 text-blue-800" },
      prepaid: { label: "Prepagado", color: "bg-orange-100 text-orange-800" },
      payment_to_check: {
        label: "Verificar Pago",
        color: "bg-yellow-100 text-yellow-800",
      },
      accepted: { label: "Aceptado", color: "bg-green-100 text-green-800" },
      in_transit: {
        label: "En Tránsito",
        color: "bg-blue-100 text-blue-800",
      },
      delivered: {
        label: "Entregado",
        color: "bg-purple-100 text-purple-800",
      },
      rejected: { label: "Rechazado", color: "bg-red-100 text-red-800" },
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    if (!config) return null;

    return <Badge className={`${config.color} border-0`}>{config.label}</Badge>;
  };

  // Check if dispatch guide can be created
  const canCreateDispatchGuide = (order: SalesRequest) => {
    return !["rejected", "test"].includes(order.status);
  };

  // Check if order can be modified (only dispatch_ready status)
  const canModifyOrder = (order: SalesRequest) => {
    return order.status === "dispatch_ready";
  };

  const handleRowClick = async (order: SalesRequest) => {
    setSelectedOrder(order);
    setLoadingOrderItems(true);
    setCurrentOrderItems([]);

    try {
      // Fetch items for this specific order
      const items = await fetchItemsForSalesRequest(order.id);
      setCurrentOrderItems(items);
    } catch (error) {
      console.error("Error fetching order items:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los productos de la orden",
        variant: "destructive",
      });
    } finally {
      setLoadingOrderItems(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader
          title="Gestión de Guías de Despacho"
          description="Crea y gestiona guías de despacho para las notas de venta"
        />
        <StatCardsSkeleton count={5} className="lg:grid-cols-5" />
        <TableSkeleton rows={8} columns={6} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Gestión de Guías de Despacho"
        description="Crea y gestiona guías de despacho para las notas de venta"
        actions={
          <Button
            onClick={handleCreateDialogOpen}
            className="h-9 bg-blue-600 text-white hover:bg-blue-700"
          >
            <Plus className="mr-2 h-4 w-4" />
            Crear Guía
          </Button>
        }
      />

      {/* Statistics */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-amber-600">
              {
                filteredAndSortedOrders.filter((o) =>
                  ["accepted", "por_facturar"].includes(o.status)
                ).length
              }
            </div>
            <div className="text-sm text-muted-foreground">Para despacho</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">
              {
                filteredAndSortedOrders.filter(
                  (o) => o.status === "dispatch_ready"
                ).length
              }
            </div>
            <div className="text-sm text-muted-foreground">Con Guía</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">
              {salesRequests.filter((o) => o.status === "in_transit").length}
            </div>
            <div className="text-sm text-muted-foreground">En tránsito</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-600">
              {salesRequests.filter((o) => o.status === "delivered").length}
            </div>
            <div className="text-sm text-muted-foreground">Entregados</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(
                filteredAndSortedOrders.reduce(
                  (sum, o) => sum + grossFromNet(o.totalAmount, o.isExento),
                  0
                )
              )}
            </div>
            <div className="text-sm text-muted-foreground">Valor total</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
            <div className="relative w-full sm:min-w-[240px] sm:flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por cliente o ID de nota..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 w-full pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-full sm:w-[220px]">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="ready_for_dispatch">
                  Listos para despacho
                </SelectItem>
                <SelectItem value="pending">Pendientes</SelectItem>
                <SelectItem value="dispatch_ready">Con Guía</SelectItem>
                <SelectItem value="prepaid">Prepagados</SelectItem>
                <SelectItem value="payment_to_check">Verificar Pago</SelectItem>
                <SelectItem value="accepted">Aceptados</SelectItem>
                <SelectItem value="in_transit">En Tránsito</SelectItem>
                <SelectItem value="delivered">Entregados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer select-none whitespace-nowrap hover:bg-muted/50"
                    onClick={() => handleSort("id")}
                  >
                    <div className="flex items-center gap-1">
                      ID
                      {sortColumn === "id" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        ))}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none whitespace-nowrap hover:bg-muted/50"
                    onClick={() => handleSort("client")}
                  >
                    <div className="flex items-center gap-1">
                      Cliente
                      {sortColumn === "client" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        ))}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none whitespace-nowrap hover:bg-muted/50"
                    onClick={() => handleSort("createdAt")}
                  >
                    <div className="flex items-center gap-1">
                      Fecha
                      {sortColumn === "createdAt" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        ))}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none whitespace-nowrap hover:bg-muted/50"
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center gap-1">
                      Estado
                      {sortColumn === "status" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        ))}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none whitespace-nowrap text-right hover:bg-muted/50"
                    onClick={() => handleSort("amount")}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Monto
                      {sortColumn === "amount" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        ))}
                    </div>
                  </TableHead>
                  <TableHead className="text-center">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center">
                      <div className="text-muted-foreground">
                        No se encontraron notas de venta
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedOrders.map((order, index) => (
                    <TableRow
                      key={order.id}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                      className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
                      onClick={() => handleRowClick(order)}
                    >
                      <TableCell className="font-mono text-sm">
                        {order.id.slice(0, 8)}...
                      </TableCell>
                      <TableCell className="font-medium">
                        {getClientName(order.clientId)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {format(new Date(order.createdAt), "dd/MM/yyyy", {
                            locale: es,
                          })}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(order.status)}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(
                          grossFromNet(order.totalAmount, order.isExento)
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-center">
                        {canCreateDispatchGuide(order) ? (
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setGuiaDialogOrder(order);
                            }}
                            disabled={isCreatingDispatchGuide === order.id}
                            className="bg-green-500 text-white hover:bg-green-700"
                          >
                            {isCreatingDispatchGuide === order.id ? (
                              "Creando..."
                            ) : (
                              <>
                                <FileText className="mr-2 h-4 w-4" />
                                Crear Guía
                              </>
                            )}
                          </Button>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            No disponible
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-muted-foreground">
            Mostrando {startIndex + 1}-
            {Math.min(
              startIndex + itemsPerPage,
              filteredAndSortedOrders.length
            )}{" "}
            de {filteredAndSortedOrders.length} resultados
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  // Show first page, last page, current page, and pages around current
                  return (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  );
                })
                .map((page, index, visiblePages) => {
                  // Add ellipsis between non-consecutive pages
                  const elements = [];
                  if (index > 0 && visiblePages[index - 1] < page - 1) {
                    elements.push(
                      <span
                        key={`ellipsis-${page}`}
                        className="px-2 text-muted-foreground"
                      >
                        ...
                      </span>
                    );
                  }
                  elements.push(
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className="h-8 w-8 p-0"
                    >
                      {page}
                    </Button>
                  );
                  return elements;
                })
                .flat()}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {/* Order Detail Dialog */}
      {selectedOrder && (
        <OrderDetailDialog
          selectedOrder={selectedOrder}
          setSelectedOrder={setSelectedOrder}
          getSellerInfo={getSellerInfo}
          getApproverRejecterInfo={getApproverRejecterInfo}
          clientNames={clientNames}
          items={currentOrderItems}
          onOrderStatusChange={fetchSalesRequests}
          clientRiskStatus={clientRiskStatus}
          clientInfo={clientInfo}
        />
      )}

      {guiaDialogOrder && (
        <CreateDispatchGuideDialog
          isOpen={!!guiaDialogOrder}
          onClose={() => setGuiaDialogOrder(null)}
          onConfirm={handleConfirmGuia}
          selectedOrder={guiaDialogOrder}
          isCreating={isCreatingDispatchGuide === guiaDialogOrder.id}
        />
      )}

      {/* Create Order Dispatch Dialog */}
      {createDialogOpen && (
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Crear Guía de Despacho</DialogTitle>
              <DialogDescription>
                Busca y selecciona un cliente para crear una nueva guía de
                despacho.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="clientSearch" className="text-right">
                  Buscar
                </Label>
                <div className="col-span-3">
                  <Input
                    id="clientSearch"
                    placeholder="Buscar cliente por nombre o RUT..."
                    value={clientSearch}
                    onChange={(e) => handleClientSearchChange(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 items-start gap-4">
                <Label className="pt-2 text-right">Clientes</Label>
                <div className="col-span-3">
                  <ScrollArea className="h-[200px] w-full rounded-md border p-2">
                    {clientsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <span className="text-sm text-muted-foreground">
                          Cargando clientes...
                        </span>
                      </div>
                    ) : filteredClients.length === 0 ? (
                      <div className="flex items-center justify-center py-4">
                        <span className="text-sm text-muted-foreground">
                          {clientSearch.length >= 2
                            ? "No se encontraron clientes"
                            : "Escribe para buscar clientes"}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col space-y-1">
                        {filteredClients.map((client) => (
                          <Button
                            key={client.id}
                            variant="ghost"
                            size="sm"
                            className="h-auto justify-start p-2"
                            onClick={() =>
                              handleClientSelect(String(client.id))
                            }
                          >
                            <div className="text-left">
                              <div className="font-medium">{client.name}</div>
                              {client.taxId && (
                                <div className="text-xs text-muted-foreground">
                                  RUT: {client.taxId}
                                </div>
                              )}
                            </div>
                          </Button>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
              >
                Cancelar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Purchase Order Drawer */}
      {purchaseOrderDrawerOpen && selectedClient && (
        <>
          {console.log(
            "Rendering CreatePurchaseOrderDrawer with client:",
            selectedClient
          )}
          <CreatePurchaseOrderDrawer
            client={selectedClient}
            open={purchaseOrderDrawerOpen}
            onOpenChange={(open) => {
              setPurchaseOrderDrawerOpen(open);
              if (!open) {
                setSelectedClient(null);
                setSelectedClientId("");
                handleOrderCreated();
              }
            }}
          />
        </>
      )}
    </PageContainer>
  );
};

export default GuiasDespachoPage;
